import io from "socket.io-client";
import toast from "react-hot-toast";

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10; // increased for better reliability
    this.reconnectDelay = 1000;
    this.userId = null;
    this.listeners = new Map();
    this.isReconnecting = false;
    this.connectionTimeout = null;
    this.reconnectTimeout = null;
    this.lastConnectionTime = null;

    // track if this is a page refresh scenario
    this.isPageRefresh = false;

    // track currently open conversation ID to avoid showing notifications for active conversations
    this.currentConversationId = null;

    // setup page visibility and navigation tracking
    this.setupPageTracking();
  }

  // setup page visibility and navigation tracking
  setupPageTracking() {
    // detect page refresh vs navigation
    const navigationEntry = performance.getEntriesByType("navigation")[0];
    if (navigationEntry && navigationEntry.type === "reload") {
      this.isPageRefresh = true;
    }

    // track page visibility changes
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && this.userId) {
        this.handlePageVisible();
      }
    });

    // track beforeunload (page refresh/close)
    window.addEventListener("beforeunload", () => {
      this.handlePageUnload();
    });

    // track pagehide (navigation away)
    window.addEventListener("pagehide", () => {
      // don't disconnect for navigation, only for refresh/close
    });

    // track online/offline status
    window.addEventListener("online", () => {
      if (this.userId && !this.isConnected) {
        this.reconnect();
      }
    });

    window.addEventListener("offline", () => {
      // Network went offline
    });
  }

  // handle page becoming visible
  handlePageVisible() {
    if (this.userId && !this.isConnected && !this.isReconnecting) {
      this.reconnect();
    } else if (this.userId && this.isConnected) {
      // Re-announce online presence so the backend re-broadcasts status to contacts
      this.emitUserOnline();
    }
  }

  // handle page unload (refresh/close)
  handlePageUnload() {
    if (this.socket && this.isConnected) {
      // emit user offline status
      this.socket.emit("userOffline", { userId: this.userId });

      // disconnect socket
      this.socket.disconnect();

      // clear timeouts
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
    }
  }

  // Initialize socket connection
  connect(userId) {
    if (this.socket && this.isConnected) {
      return;
    }

    if (this.isReconnecting) {
      return;
    }

    this.userId = userId;
    this.lastConnectionTime = Date.now();

    try {
      this.socket = io(process.env.REACT_APP_SOCKET_URL, {
        query: { userId }, // Send userId as query parameter
        transports: ["websocket", "polling"],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
        timeout: 20000,
        forceNew: true, // force new connection
      });

      this.setupEventListeners();

      // set connection timeout
      this.connectionTimeout = setTimeout(() => {
        if (!this.isConnected) {
          this.handleReconnection();
        }
      }, 25000); // 25 seconds timeout
    } catch (error) {
      this.handleReconnection();
    }
  }

  // Setup socket event listeners
  setupEventListeners() {
    if (!this.socket) return;

    // Connection events
    this.socket.on("connect", () => {
      this.isConnected = true;
      this.isReconnecting = false;
      this.reconnectAttempts = 0;

      // clear connection timeout
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }

      this.emitUserOnline();
      this.notifyListeners("connection", { connected: true });

      // reset page refresh flag after successful connection
      if (this.isPageRefresh) {
        this.isPageRefresh = false;
      }
    });

    this.socket.on("disconnect", (reason) => {
      this.isConnected = false;

      // clear connection timeout
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }

      this.notifyListeners("connection", { connected: false, reason });

      if (reason === "io server disconnect") {
        // Server disconnected us, try to reconnect
        this.socket.connect();
      } else if (reason === "io client disconnect") {
        // Client disconnected, don't auto-reconnect
      } else {
        // Other reasons, attempt reconnection
        this.handleReconnection();
      }
    });

    this.socket.on("connect_error", (error) => {
      this.isConnected = false;
      this.notifyListeners("connection", { connected: false, error });
      this.handleReconnection();
    });

    this.socket.on("reconnect", (attemptNumber) => {
      this.isConnected = true;
      this.isReconnecting = false;
      this.reconnectAttempts = 0;
      this.emitUserOnline();
      this.notifyListeners("connection", {
        connected: true,
        reconnected: true,
      });
    });

    this.socket.on("reconnect_attempt", (attemptNumber) => {
      this.isReconnecting = true;
      this.reconnectAttempts = attemptNumber;
      this.notifyListeners("connection", {
        connected: false,
        reconnecting: true,
        attempt: attemptNumber,
      });
    });

    this.socket.on("reconnect_failed", () => {
      this.isReconnecting = false;
      this.notifyListeners("connection", {
        connected: false,
        reconnectionFailed: true,
      });

      // try manual reconnection after a delay
      setTimeout(() => {
        if (this.userId && !this.isConnected) {
          this.reconnect();
        }
      }, 5000);
    });

    // Chat events - Updated to match backend
    this.socket.on("messageReceived", (data) => {
      // console.log("messageReceived-------->", data);
      // Only emit messageReceived event to avoid duplicates
      this.notifyListeners("messageReceived", data);
    });

    this.socket.on("userStatusUpdate", (data) => {
      this.notifyListeners("userStatusUpdate", data);
    });

    this.socket.on("updateConversation", (data) => {
      // console.log("updateConversation-------->", data);
      // Notify conversation update listeners
      this.notifyListeners("conversationUpdate", data);

      // Check if this conversation update contains sub-conversations and trigger sub-conversations refresh
      if (data && data.batch && data.batch.length > 0) {
        const conversationUpdate = data.batch[0].updateConversation;
        if (
          conversationUpdate &&
          conversationUpdate.subConversations &&
          conversationUpdate.subConversations.length > 0
        ) {
          // Emit event to refresh sub-conversations for this conversation
          this.notifyListeners("subConversationsUpdate", {
            conversationId: conversationUpdate._id,
            subConversationIds: conversationUpdate.subConversations,
            conversationData: conversationUpdate,
          });

          // Also trigger a manual fetch of sub-conversations if needed
          if (this.socket && this.isConnected) {
            this.socket.emit("getSubConversations", {
              conversationId: conversationUpdate._id,
              userId: this.userId,
            });
          }
        }
      }
    });

    this.socket.on("typingIndicator", (data) => {
      this.notifyListeners("typing", data);
    });

    this.socket.on("messagesDelivered", (data) => {
      this.notifyListeners("messagesDelivered", data);
    });

    this.socket.on("messagesRead", (data) => {
      this.notifyListeners("messagesRead", data);
    });

    // Listen for message edited events
    this.socket.on("messageEdited", (data) => {
      // console.log("messageEdited----->", data);
      this.notifyListeners("messageEdited", data);
    });

    // Backend emits conversationList after getConversations
    this.socket.on("conversationList", (data) => {
      // console.log("conversationList----->", data);
      this.notifyListeners("conversationList", data);
    });

    // Backend emits messagesFetched after getMessages
    this.socket.on("messagesFetched", (data) => {
      // console.log("messagesFetched----->", data);
      this.notifyListeners("messagesFetched", data);
    });

    // Backend emits messagesFetchedSubConversation after getMessagesSubConversation
    this.socket.on("messagesFetchedSubConversation", (data) => {
      // console.log("messagesFetchedSubConversation", data);
      this.notifyListeners("messagesFetchedSubConversation", data);
    });

    // Backend emits messageSentOnSubConversation after sendMessageOnSubConversation
    this.socket.on("messageSentOnSubConversation", (data) => {

      this.notifyListeners("messageSentOnSubConversation", data);
    });

    // Backend emits messageReceivedSubConversation for receiving sub-conversation messages on opposite side
    this.socket.on("messageReceivedSubConversation", (data) => {
      // console.log("messageReceivedSubConversation---->", data);
      this.notifyListeners("messageReceivedSubConversation", data);
    });

    // Backend emits messageSentOnMiniConversation after sendMessageOnMiniConversation
    this.socket.on("messageSentOnMiniConversation", (data) => {
      this.notifyListeners("messageSentOnMiniConversation", data);
    });

    // Backend emits messageReceivedMiniConversation for receiving mini-conversation messages on opposite side
    this.socket.on("messageReceivedMiniConversation", (data) => {
      this.notifyListeners("messageReceivedMiniConversation", data);
    });

    // Backend emits messagesFetchedMiniConversation after getMessagesMiniConversation
    this.socket.on("messagesFetchedMiniConversation", (data) => {
      // console.log("messagesFetchedMiniConversation", data);
      this.notifyListeners("messagesFetchedMiniConversation", data);
    });

    // Backend emits miniConversationInfo after getMiniConversationInfo
    this.socket.on("miniConversationInfo", (data) => {
      // console.log("miniConversationInfo", data);
      this.notifyListeners("miniConversationInfo", data);
    });

    // Backend emits conversationsInfo after getConversationsInfo
    this.socket.on("conversationsInfo", (data) => {
      // console.log("conversationsInfo-------->", data);
      this.notifyListeners("conversationsInfo", data);
    });

    // Backend emits subConversations after getSubConversations
    this.socket.on("subConversations", (data) => {
      this.notifyListeners("subConversations", data);
    });

    // Backend emits subConversationUpdated when a sub-conversation is updated
    this.socket.on("subConversationUpdated", (data) => {
      this.notifyListeners("subConversationUpdated", data);
    });

    // Backend emits subConversationInfo after getSubConversationInfo
    this.socket.on("subConversationInfo", (data) => {
      this.notifyListeners("subConversationInfo", data);
    });

    this.socket.on("error", (error) => {
      this.notifyListeners("error", error);
    });

    // Listen for unread conversations count
    this.socket.on("unreadConversationsCount", (data) => {
      this.notifyListeners("unreadConversationsCount", data);
    });

    // Backend emits MessageInfo after getMessageInfo
    this.socket.on("MessageInfo", (data) => {
      console.log("MessageInfo received in socketService-------->", data);
      this.notifyListeners("MessageInfo", data);
    });

    // Global notification listener - shows toast notifications for messages
    // Only shows notification if the conversation is not currently open
    this.socket.on("notification", (data) => {
      console.log("notification received-------->", data);
      
      // Extract notification data
      const { message, title, metaData } = data || {};
      
      // Check if this notification is for the currently open conversation
      let shouldShowNotification = true;
      
      // Try to extract conversationId from various possible locations
      let notificationConversationId = null;
      
      if (metaData) {
        // Check if metaData contains conversationId
        notificationConversationId = 
          metaData.conversationId || 
          metaData.conversation_id || 
          metaData._id ||
          metaData.message?.conversationId ||
          metaData.message?.conversation_id;
      }
      
      // Also check if message itself has conversationId
      if (!notificationConversationId && data?.message) {
        notificationConversationId = 
          data.message.conversationId || 
          data.message.conversation_id ||
          data.message._id;
      }
      
      // If we have a current conversation open and this notification is for it, don't show
      if (this.currentConversationId && notificationConversationId) {
        // Compare as strings to handle different types
        if (String(this.currentConversationId) === String(notificationConversationId)) {
          shouldShowNotification = false;
        }
      }
      
      // Show toast notification if conversation is not currently open
      if (shouldShowNotification && message) {
        // Show toast with title if available, otherwise just message
        const notificationMessage = title ? `${title}: ${message}` : message;
        if(metaData?.userPic){
          toast.custom((t) => (
            <div style={{
              padding: "12px 16px",
              cursor: "pointer",
              transition: "background-color 0.2s",
              backgroundColor: "#fff",
              borderRadius: "10px",
              width: "300px",
              boxShadow: "0px 4px 10px -4px rgb(58 53 65 / 60%)"
            }}>
              <div style={{ 
                display: "flex",
                gap: "0.5rem",
                alignItems: "flex-start",
              }}>
                <div style={{ 
                  display: "flex",
                  alignItems: "center",
                  width: "40px",
                  height: "40px",
                  backgroundColor:"#9C27B0",
                  borderRadius: "20px",
                 }}>
                  <img
                    src={metaData?.userPic}
                    style={{ 
                      borderRadius: "20px",
                      height: "40px",
                      width: "40px",
                     }}
                    alt="user"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                </div>
                <div style={{ 
                  flex: 1,
                  minWidth: 0
                 }}>
                  {title && (<p style={{ 
                    margin: 0,
                    fontFamily: "Inter, sans-serif, -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif",
                    fontWeight: "400",
                    fontSize: "0.875rem",
                    lineHeight: "1.5",
                    letterSpacing: "0.15px",
                    color: "rgba(33, 37, 41)",
                    color: "#9C27B0",
                    fontWeight: "600",
                    marginBottom: "0.125rem",
                   }} >{title}</p>)}
                  <p style={{ 
                    margin: 0,
                    fontFamily: "Inter, sans-serif, -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif",
                    fontWeight: "400",
                    lineHeight: "1.5",
                    letterSpacing: "0.15px",
                    color: "rgba(33, 37, 41)",
                    fontWeight: "400",
                    fontSize: "0.8125rem",
                   }}>{message}</p>
                </div>
              </div>
            </div>
          ), {
            position: "top-right",
            duration: 4000,
          });
        }else{
          toast(notificationMessage, {
            duration: 4000,
            position: "top-right",
          });
        }
      }
      
      // Also notify listeners for any components that want to handle notifications
      this.notifyListeners("notification", data);
    });
  }

  // Emit user online status
  emitUserOnline() {
    if (this.socket && this.isConnected && this.userId) {
      this.socket.emit("userOnline", { userId: this.userId });
      this.socket.emit("getConversations", { userId: this.userId });
      // Request unread conversations count
      this.getUnreadConversations();
    }
  }

  // Get unread conversations count
  getUnreadConversations() {
    if (this.socket && this.isConnected && this.userId) {
      this.socket.emit("getUnreadConversations", { userId: this.userId });
      return true;
    }
    return false;
  }

  // Send message - Updated to match backend
  sendMessage(messageData) {
    // console.log("sendMessage-------->", messageData);
    if (this.socket && this.isConnected) {
      // Send message immediately without any artificial delays
      this.socket.emit("sendMessage", {
        ...messageData,
        // senderId: messageData.senderId || this.userId,
        timestamp: new Date().toISOString(),
      });

      return true;
    }
    return false;
  }

  // Send message on sub-conversation - New method for sub-conversations
  sendMessageOnSubConversation(messageData) {
    if (this.socket && this.isConnected) {

      // Send message on sub-conversation with required payload structure
      // FIXED: Use senderId from messageData if provided (for AI messages), otherwise use current user ID
      this.socket.emit("sendMessageOnSubConversation", {
        _id: messageData._id,
        senderId: messageData.senderId || this.userId,
        conversationId: messageData.conversationId,
        conversationType: "SubConversation",
        type: messageData.type || "text",
        message: messageData.message,
        attachments: messageData.attachments || [],
        mentions: messageData.mentions || [],
        reactions: messageData.reactions || [],
        replyTo: messageData.replyTo || null, // Ensure replyTo is included
        repliedBy: messageData.repliedBy || [],
        forwardedFrom: messageData.forwardedFrom || null,
        forwardedBy: messageData.forwardedBy || [],
        isDeletedForMe: messageData.isDeletedForMe || [],
        isDeletedForEveryone: messageData.isDeletedForEveryone || false,
        isEdited: messageData.isEdited || false,
        editedBy: messageData.editedBy || [],
        deliveredTo: messageData.deliveredTo || [],
        readBy: messageData.readBy || [],
        location: messageData.location || null,
        latLong: messageData.latLong || null,
        orderId: messageData.orderId || null,
      });

      return true;
    }
    return false;
  }

  // Send message on mini-conversation - New method for mini conversations
  sendMessageOnMiniConversation(messageData) {
    if (this.socket && this.isConnected) {
      // Send message on mini-conversation with required payload structure
      // Use senderId from messageData if provided (for AI messages), otherwise use current user ID
      this.socket.emit("sendMessageOnMiniConversation", {
        _id: messageData._id,
        senderId: messageData.senderId || this.userId,
        conversationId: messageData.conversationId,
        miniConversationId: messageData.miniConversationId,
        conversationType: "miniConversation",
        type: messageData.type || "text",
        message: messageData.message,
        attachments: messageData.attachments || [],
        mentions: messageData.mentions || [],
        reactions: messageData.reactions || [],
        replyTo: messageData.replyTo || null,
        repliedBy: messageData.repliedBy || [],
        forwardedFrom: messageData.forwardedFrom || null,
        forwardedBy: messageData.forwardedBy || [],
        isDeletedForMe: messageData.isDeletedForMe || [],
        isDeletedForEveryone: messageData.isDeletedForEveryone || false,
        isEdited: messageData.isEdited || false,
        editedBy: messageData.editedBy || [],
        deliveredTo: messageData.deliveredTo || [],
        readBy: messageData.readBy || [],
        location: messageData.location || null,
        latLong: messageData.latLong || null,
        orderId: messageData.orderId || null,
      });

      return true;
    }
    return false;
  }

  // Join conversation room - Updated to match backend
  joinConversation(conversationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit("conversationOpened", { conversationId });
      // Track current conversation to avoid showing notifications for active conversations
      this.currentConversationId = conversationId;
      return true;
    }
    return false;
  }

  // Leave conversation room - Updated to match backend
  leaveConversation(conversationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit("conversationOpened", { conversationId: null });
      // Clear current conversation tracking
      this.currentConversationId = null;
      return true;
    }
    return false;
  }

  // Set current conversation ID (for tracking which conversation is open)
  setCurrentConversationId(conversationId) {
    this.currentConversationId = conversationId;
  }

  // Get current conversation ID
  getCurrentConversationId() {
    return this.currentConversationId;
  }

  // Emit typing status - Updated to match backend
  emitTyping(conversationId, isTyping) {
    if (this.socket && this.isConnected) {
      this.socket.emit("typing", {
        conversationId,
        userName: "User", // You can pass actual username here
        isTyping: isTyping ? 1 : 0, // Backend expects number
      });
      return true;
    }
    return false;
  }

  // Get conversations - New method to match backend
  getConversations() {
    if (this.socket && this.isConnected && this.userId) {
      this.socket.emit("getConversations", { userId: this.userId });
      return true;
    }
    return false;
  }

  // Get messages - New method to match backend
  getMessages(conversationId, lastMessageId = null) {
    if (this.socket && this.socket.connected) {
      this.socket.emit("getMessages", { conversationId, lastMessageId });
      return true;
    } else {
      return false;
    }
  }

  // Get messages for mini conversation
  getMessagesMiniConversation(conversationId, lastMessageId = null) {
    if (this.socket && this.socket.connected) {
      this.socket.emit("getMessagesMiniConversation", { conversationId, lastMessageId });
      return true;
    } else {
      return false;
    }
  }

  // Get sub-conversations for a specific conversation - New method to match backend
  getSubConversations(conversationId) {
    if (this.socket && this.isConnected && this.userId) {
      this.socket.emit("getSubConversations", {
        conversationId,
        userId: this.userId,
      });
      return true;
    } else {
      return false;
    }
  }

  // Refresh sub-conversations for a specific conversation - Useful for manual refresh
  refreshSubConversations(conversationId) {
    if (this.socket && this.isConnected && this.userId) {
      // First emit the subConversationsUpdate event to notify listeners
      this.notifyListeners("subConversationsUpdate", {
        conversationId: conversationId,
        action: "manualRefresh",
      });

      // Then request fresh data from server
      this.socket.emit("getSubConversations", {
        conversationId,
        userId: this.userId,
      });
      return true;
    } else {

      return false;
    }
  }

  // Mark conversation as read - New method to match backend
  markAsRead(conversationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit("markAsRead", { conversationId });
      return true;
    }
    return false;
  }

  // Handle reconnection with exponential backoff
  handleReconnection() {
    if (
      this.reconnectAttempts < this.maxReconnectAttempts &&
      !this.isReconnecting
    ) {
      this.isReconnecting = true;

      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);

      this.reconnectTimeout = setTimeout(() => {
        if (this.socket && this.userId) {
          this.socket.connect();
        }
      }, delay);

      this.reconnectAttempts++;
    } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.isReconnecting = false;

      // try manual reconnection after a longer delay
      setTimeout(() => {
        if (this.userId && !this.isConnected) {
          this.reconnect();
        }
      }, 10000);
    }
  }

  // Manual reconnection method
  reconnect() {
    if (this.isReconnecting) {
      return;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket.connect();
    } else if (this.userId) {
      this.connect(this.userId);
    }
  }

  // Disconnect socket
  disconnect() {
    // clear timeouts
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      // Emit user offline before disconnecting
      if (this.isConnected && this.userId) {
        this.socket.emit("userOffline", { userId: this.userId });
      }

      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.isReconnecting = false;
      this.userId = null;
    }
  }

  // Event listener management
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    const eventListeners = this.listeners.get(event);

    // Check if this exact callback is already registered
    if (eventListeners.has(callback)) {
      return () => {}; // Return empty cleanup function
    }

    eventListeners.add(callback);

    // Return unsubscribe function
    return () => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
        if (eventListeners.size === 0) {
          this.listeners.delete(event);
        }
      }
    };
  }

  removeEventListener(event, callback) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
      if (eventListeners.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  notifyListeners(event, data) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ Error in ${event} listener:`, error);
        }
      });
    }
  }

  // Get listener statistics for debugging
  getListenerStats() {
    const stats = {};
    this.listeners.forEach((listeners, event) => {
      stats[event] = listeners.size;
    });
    return stats;
  }

  // Clear all listeners for a specific event
  clearEventListeners(event) {
    if (this.listeners.has(event)) {
      const count = this.listeners.get(event).size;
      this.listeners.delete(event);
      return count;
    }
    return 0;
  }

  // Clear all listeners
  clearAllListeners() {
    const totalListeners = Array.from(this.listeners.values()).reduce(
      (sum, listeners) => sum + listeners.size,
      0
    );
    this.listeners.clear();
    return totalListeners;
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      isReconnecting: this.isReconnecting,
      reconnectAttempts: this.reconnectAttempts,
      userId: this.userId,
      lastConnectionTime: this.lastConnectionTime,
      isPageRefresh: this.isPageRefresh,
    };
  }

  // Check if socket is connected
  isSocketConnected() {
    return this.isConnected && this.socket !== null;
  }

  // Force reconnection (for page refresh scenarios)
  forceReconnect() {
    const savedUserId = this.userId;
    if (savedUserId) {
      this.disconnect();
      setTimeout(() => {
        this.connect(savedUserId);
      }, 100);
      return true;
    }
    return false;
  }

  // Manual connection retry
  retryConnection() {
    if (this.userId && !this.isConnected && !this.isReconnecting) {
      this.reconnect();
      return true;
    }
    return false;
  }

  // Get conversation last updated data
  getConversationLastUpdated(conversationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit("getConversationLastUpdated", { conversationId });
      return true;
    }
    return false;
  }

  // Get conversation details
  getConversationDetails(conversationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit("getConversationDetails", { conversationId });
      return true;
    }
    return false;
  }

  // Generic emit method for custom events
  emit(eventName, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(eventName, data);
      return true;
    } else {
      console.warn(`⚠️ Cannot emit ${eventName}: socket not connected`, {
        hasSocket: !!this.socket,
        isConnected: this.isConnected,
      });
      return false;
    }
  }

  // Get detailed connection status
  getDetailedConnectionStatus() {
    return {
      isConnected: this.isConnected,
      isReconnecting: this.isReconnecting,
      reconnectAttempts: this.reconnectAttempts,
      userId: this.userId,
      socketConnected: this.socket?.connected || false,
      socketId: this.socket?.id || null,
      maxReconnectAttempts: this.maxReconnectAttempts,
      reconnectDelay: this.reconnectDelay,
      lastConnectionTime: this.lastConnectionTime,
      isPageRefresh: this.isPageRefresh,
    };
  }

  // Test server connectivity
  async testServerConnectivity() {
    try {
      // Try to ping the server
      if (this.socket && this.isConnected) {
        return { success: true, message: "socket is connected" };
      } else if (this.socket) {
        return { success: false, message: "socket exists but not connected" };
      } else {
        return { success: false, message: "no socket instance" };
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  }

  // Check if connection is stale (for long-running connections)
  isConnectionStale() {
    if (!this.lastConnectionTime) return false;
    const now = Date.now();
    const staleThreshold = 30 * 60 * 1000; // 30 minutes
    return now - this.lastConnectionTime > staleThreshold;
  }

  // Refresh connection if stale
  refreshStaleConnection() {
    if (this.isConnectionStale() && this.userId) {
      this.forceReconnect();
      return true;
    }
    return false;
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
